// src/sim/lighting/light-level.ts — pure lightLevelAt query (VIS-04, VIS-07).
//
// Invariants:
//   - Pure function: no mutation, no side effects, no timers.
//   - Returns [0, 1] — sums permanent + motion + torch contributions, then clamps.
//   - Wall-respecting: cheap grid-walk LOS check (good enough for the player entity in P2).
//
// Source: .planning/phases/02-fov-lighting/02-RESEARCH.md §Pattern 4.

import type { Vec2, MapData } from '../../shared/types/map';
import type { LightsState } from '../../shared/types/world';
import { tileAt } from '../map/grid';
import { getBit } from './bitset';
import { P2_MOTION_LIGHT_FADE_TICKS, P2_TORCH_LIGHT_CONTRIBUTION } from './constants';

export interface TorchLightSource {
  origin: Vec2;
  facingRad: number;
  fovBitset: Uint32Array;
}

/**
 * Composes permanent + motion + torch contributions at a world-pixel position
 * into a single [0,1] light level. Sums contributions and clamps at 1.
 *
 * Permanent lights: always contribute by linear radial falloff (clamped at 0).
 * Motion lights: contribute the same way IF active; if fading, scaled by remaining fade fraction.
 *                 Skipped entirely when there is no entry in lightsState (never activated).
 * Torch: contributes a flat P2_TORCH_LIGHT_CONTRIBUTION when the position's tile is in the FOV bitset.
 */
export function lightLevelAt(
  pos: Vec2,
  mapData: MapData,
  lightsState: LightsState,
  torches: ReadonlyArray<TorchLightSource>,
  currentTick: number,
): number {
  let total = 0;

  for (const L of mapData.lights) {
    // Skip motion lights that have never been activated — no entry in lightsState.
    if (L.type === 'motion' && !lightsState[L.id]) continue;

    const distPx = Math.hypot(pos.x - L.position.x, pos.y - L.position.y);
    const radiusPx = L.radius * mapData.tileWidth;
    if (distPx >= radiusPx) continue;
    if (isBlockedByWall(pos, L.position, mapData)) continue;

    let contribution = (1 - distPx / radiusPx) * L.intensity;
    if (L.type === 'motion') {
      const state = lightsState[L.id];
      // Already filtered out above; this guard satisfies type-narrowing
      if (!state) continue;
      if (state.fadingTick !== null) {
        const fadeProgress = (currentTick - state.fadingTick) / P2_MOTION_LIGHT_FADE_TICKS;
        contribution *= Math.max(0, 1 - fadeProgress);
      }
    }
    total += contribution;
  }

  // Torch contribution: tile-space lookup against the precomputed FOV bitset.
  for (const T of torches) {
    const tx = Math.floor(pos.x / mapData.tileWidth);
    const ty = Math.floor(pos.y / mapData.tileHeight);
    const idx = ty * mapData.widthInTiles + tx;
    if (getBit(T.fovBitset, idx)) {
      total += P2_TORCH_LIGHT_CONTRIBUTION;
    }
  }

  return Math.min(1, total);
}

/** Cheap 1-step grid walk for wall respect. Good enough for P2; P4 may upgrade. */
function isBlockedByWall(a: Vec2, b: Vec2, map: MapData): boolean {
  const steps = Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / map.tileWidth);
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    const tx = Math.floor((a.x + (b.x - a.x) * t) / map.tileWidth);
    const ty = Math.floor((a.y + (b.y - a.y) * t) / map.tileHeight);
    if (tileAt(map.grid, tx, ty) === 1) return true;
  }
  return false;
}
